import DixaIcon from '@dixa/dixa-modern-icons';
import { Timestamp, Tooltip } from '@dixa/lib-shared-habla';
import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { Component } from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';

import { numberFormatterInternational } from '../../../../app/utils/telephonyHelpers';
import Spinner from '../../../ajax/components/Spinner';

import Dialer from './Dialer';

import '../styles/phone-control.less';

type Props = {
  meta: {
    asyncStatus: AsyncStatus;
    usersAsyncStatus: AsyncStatus;
    dynamicTelephoneNumbersArrayAsyncStatus: AsyncStatus;
    allCountryCodesAsyncStatus: AsyncStatus;
    mute: AsyncStatus;
    hold: AsyncStatus;
  };
  isOnHold: boolean;
  isOnMute: boolean;
  isOnTrial: boolean;
  metaConversation: MetaConversation;
  isInternalCall: boolean;
  hasAddCaller: boolean;

  telephonySetMute: (payload: { setTo: boolean; csid: any }) => Promise<any>;
  telephonySetHold: (payload: {
    setTo: boolean;
    csid: any;
    requesterId: any;
  }) => Promise<any>;
  conversationClose: (payload: { csid: any }) => void;

  notificationAdd: (payload: any) => void;

  transferToNumberModalOpen: (metaConversation: MetaConversation) => void;
  transferToQueueModalOpen: (payload: { csid: any }) => void;
  transferToAgentModalOpen: (metaConversation: MetaConversation) => void;
  addCallerModalOpen: (metaConversation: MetaConversation) => void;
  telephonySendDigits: (digit: string) => void;
};

type StateType = {
  disabled: string;
  phoneWidgetHolderClass: string;
  dial: {
    isOpen: boolean;
    btnHolderClass: string;
    btnClass: string;
  };
  transfer: {
    isOpen: boolean;
    btnHolderClass: string;
    btnClass: string;
  };
};

export default class Phone extends Component<Props, StateType> {
  constructor(props: Props) {
    super(props);
    this.state = {
      disabled: 'disabled',
      phoneWidgetHolderClass: '',
      dial: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
      transfer: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
    };
  }

  componentWillUnmount() {
    this.setState({
      disabled: 'disabled',
      phoneWidgetHolderClass: '',
      dial: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
      transfer: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps.metaConversation.conversation.is_closed === false &&
      nextProps.metaConversation.isAssignedToMe === true
    ) {
      this.setState({ disabled: '' });
    } else {
      this.setState({ disabled: 'disabled' });
    }
  }

  componentDidMount() {
    if (
      this.props.metaConversation.conversation.is_closed === false &&
      this.props.metaConversation.isAssignedToMe === true
    ) {
      this.setState({ disabled: '' });
    }
  }

  clickHandlerHangup = (e: any) => {
    this.setState({ disabled: 'disabled' });
    e.preventDefault();
    this.props.conversationClose({
      csid: this.props.metaConversation.conversation.id,
    });
  };

  clickHandlerHold = (e: any) => {
    this.props
      .telephonySetHold({
        setTo: !this.props.isOnHold,
        csid: this.props.metaConversation.conversation.id,
        requesterId: this.props.metaConversation?.requester?.id,
      })
      .then()
      .catch(() => {
        const option = {
          type: 'mute_hold_issue',
          title: 'Hold could not be changed',
          text: 'Hold not changed',
          className: 'warning',
          icon: 'i-announcement-slash',
          time: 10000,
        };
        this.props.notificationAdd(option);
      });
    e.preventDefault();
  };

  clickHandlerMute = (e: any) => {
    this.props
      .telephonySetMute({
        setTo: !this.props.isOnMute,
        csid: this.props.metaConversation.conversation.id,
      })
      .then()
      .catch(() => {
        const option = {
          type: 'mute_hold_issue',
          title: 'Mute could not be changed',
          text: 'Mute not changed',
          className: 'warning',
          icon: 'i-announcement-slash',
          time: 10000,
        };
        this.props.notificationAdd(option);
      });
    e.preventDefault();
  };

  clickHandlerDialPad = (e: any) => {
    e.preventDefault();
    if (this.state.dial.isOpen === true) {
      this.setState({
        dial: {
          isOpen: false,
          btnHolderClass: 'animated fadeOutUp',
          btnClass: '',
        },
        transfer: {
          isOpen: false,
          btnHolderClass: 'animated fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: '',
      });
    } else {
      this.setState({
        dial: {
          isOpen: true,
          btnHolderClass: 'animated fadeInDown',
          btnClass: 'active',
        },
        transfer: {
          isOpen: false,
          btnHolderClass: 'animated fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: 'open',
      });
    }
  };

  clickHandlerTransfer = (e: any) => {
    e.preventDefault();
    if (this.state.transfer.isOpen === true) {
      this.setState({
        transfer: {
          isOpen: false,
          btnHolderClass: 'animated fadeOutUp',
          btnClass: '',
        },
        dial: {
          isOpen: false,
          btnHolderClass: 'animated fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: '',
      });
    } else {
      this.setState({
        transfer: {
          isOpen: true,
          btnHolderClass: 'animated fadeInDown',
          btnClass: 'active',
        },
        dial: {
          isOpen: false,
          btnHolderClass: 'animated fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: 'open',
      });
    }
  };

  render() {
    let isMutedActive = '';
    let isHoldActive = '';
    let phoneNumberFormat = '';
    let staticTime: any = null;
    let isRecording: JSX.Element | null = null;

    const metaConv = this.props.metaConversation;

    const phoneNumber =
      metaConv?.conversation.attributes?.called_number ??
      metaConv?.requester?.phone_number;

    if (this.props.isInternalCall === false) {
      if (phoneNumber) {
        if (phoneNumber.startsWith('+')) {
          phoneNumberFormat = numberFormatterInternational({
            phoneNumber,
          });
        } else {
          phoneNumberFormat = phoneNumber || 'anonymous';
        }
      } else {
        phoneNumberFormat = 'anonymous';
      }
    } else {
      phoneNumberFormat = 'Internal';
    }

    if (this.props.isOnMute === true) {
      isMutedActive = 'active';
    }
    if (this.props.isOnHold === true) {
      isHoldActive = 'active';
    }

    const listDigits = (digit: {
      className: string;
      input: string;
      sec_line: string;
    }) => {
      return (
        <Dialer
          className={digit.className}
          digit={digit.input}
          telephonySendDigits={this.props.telephonySendDigits}
          sec_line={digit.sec_line}
          key={digit.input}
        />
      );
    };

    let canTransferToNumberConversation = false;
    let canTransferToQueueConversation = false;
    let canTransferToAgentConversation = false;

    if (
      this.props.metaConversation.conversation.is_closed === false &&
      this.props.metaConversation.isAssignedToMe === true &&
      this.props.isInternalCall === false &&
      this.props.metaConversation.conversation.initial_channel === 'pstnphone'
    ) {
      canTransferToNumberConversation = true;
      canTransferToQueueConversation = true;
      canTransferToAgentConversation = true;
    }

    if (
      this.props.metaConversation.conversation.is_closed === false &&
      this.props.metaConversation.conversation.assignment &&
      this.props.metaConversation.conversation.assignment.assigned_at
    ) {
      staticTime =
        this.props.metaConversation.conversation.assignment.assigned_at;
    }

    if (
      this.props?.metaConversation?.conversation?.attributes
        ?.record_conversation === 'true'
    ) {
      isRecording = <DixaIcon icon="rec" />;
    }

    if (this.props.isOnTrial) {
      canTransferToNumberConversation = false;
    }

    return (
      <div className="phone-widget-container">
        <div
          className={'phone-widget-holder ' + this.state.phoneWidgetHolderClass}
        >
          <div className="always-visible-part">
            <div className="phone-widget-display">
              <h3>{phoneNumberFormat}</h3>
              {isRecording}
              <h5 id="timer">
                <Timestamp counter time={staticTime} />
              </h5>
            </div>
            <div className="main-buttons-holder">
              <Tooltip
                content={
                  <CommandKeybindingsTooltipContent commandName="conversation:phoneToggleHold" />
                }
                dark
              >
                <button
                  className={`hold-btn left ${isHoldActive} ${this.state.disabled}`}
                  onClick={
                    this.props.metaConversation.conversation.is_closed ===
                      false &&
                    this.props.metaConversation.isAssignedToMe === true
                      ? this.clickHandlerHold
                      : () => {
                          /** Noop. */
                        }
                  }
                >
                  <DixaIcon icon="pause" />
                  {this.props.meta.hold === 'loading' && (
                    <Spinner className="dark close-btn" />
                  )}
                  <span>
                    {this.props.isOnHold === true ? 'UNHOLD' : 'HOLD'}
                  </span>
                </button>
              </Tooltip>
              <Tooltip
                content={
                  <CommandKeybindingsTooltipContent commandName="conversation:close" />
                }
                dark
              >
                <button
                  className={`end-btn middle ${this.state.disabled}`}
                  onClick={
                    this.props.metaConversation.conversation.is_closed ===
                      false &&
                    this.props.metaConversation.isAssignedToMe === true
                      ? this.clickHandlerHangup
                      : () => {
                          /** Noop. */
                        }
                  }
                >
                  <DixaIcon icon="phone-hang" />
                  <span>END</span>
                </button>
              </Tooltip>
              <Tooltip
                content={
                  <CommandKeybindingsTooltipContent commandName="conversation:phoneToggleMute" />
                }
                dark
              >
                <button
                  className={`mute-btn right ${isMutedActive} ${this.state.disabled}`}
                  onClick={
                    this.props.metaConversation.conversation.is_closed ===
                      false &&
                    this.props.metaConversation.isAssignedToMe === true
                      ? this.clickHandlerMute
                      : () => {
                          /** Noop. */
                        }
                  }
                >
                  <DixaIcon icon="volume-mute" />
                  {this.props.meta.mute === 'loading' && (
                    <Spinner className="dark close-btn" />
                  )}
                  <span>
                    {this.props.isOnMute === true ? 'UNMUTE' : 'MUTE'}
                  </span>
                </button>
              </Tooltip>
              <button
                className={`reassign-btn left ${this.state.transfer.btnClass} ${
                  this.state.disabled
                } ${
                  this.props.isInternalCall === true ||
                  this.props.metaConversation.conversation.initial_channel !==
                    'pstnphone'
                    ? 'disabled'
                    : ''
                }`}
                onClick={this.clickHandlerTransfer}
              >
                <DixaIcon icon="user-plus" />
                <span>TRANSFER</span>
              </button>
              <button
                className={`dial-btn middle ${this.state.dial.btnClass} ${this.state.disabled}`}
                id="demo-dial"
                onClick={this.clickHandlerDialPad}
              >
                <DixaIcon icon="th" />
                <span>DIAL</span>
              </button>
              <button
                className={`open-btn right ${this.state.disabled}`}
                onClick={() =>
                  this.props.addCallerModalOpen(this.props.metaConversation)
                }
              >
                <DixaIcon icon="i-phone-plus" />
                <span>ADD CALL</span>
              </button>
            </div>
          </div>
          <div
            className={
              'dial-buttons-holder ' +
              this.state.dial.btnHolderClass +
              ' ' +
              (this.state.dial.isOpen === false ? '' : 'open')
            }
          >
            {[
              { input: '1', sec_line: '', className: 'btn-1 left  ' },
              { input: '2', sec_line: 'ABC', className: 'btn-2 middle  ' },
              { input: '3', sec_line: 'DEF', className: 'btn-3 right  ' },
              { input: '4', sec_line: 'GHI', className: 'btn-4 left  ' },
              { input: '5', sec_line: 'JKL', className: 'btn-5 middle  ' },
              { input: '6', sec_line: 'MNO', className: 'btn-6 right  ' },
              { input: '7', sec_line: 'PQRS', className: 'btn-7 left  ' },
              { input: '8', sec_line: 'TUV', className: 'btn-8 middle  ' },
              { input: '9', sec_line: 'WXYZ', className: 'btn-9 right  ' },
              {
                input: 'any',
                sec_line: '',
                className: 'btn-star left  last-line',
              },
              {
                input: '0',
                sec_line: '',
                className: 'btn-0 middle  last-line',
              },
              {
                input: '#',
                sec_line: '',
                className: 'btn-hash right  last-line',
              },
            ].map(listDigits)}
          </div>
          <div
            className={
              'transfer-holder ' +
              this.state.transfer.btnHolderClass +
              ' ' +
              (this.state.transfer.isOpen === false ? '' : 'open')
            }
          >
            <button
              className={
                'long-btn  ' +
                ' ' +
                (canTransferToAgentConversation === false ? 'disabled' : '')
              }
              onClick={
                canTransferToAgentConversation === true
                  ? () => {
                      this.props.transferToAgentModalOpen(
                        this.props.metaConversation,
                      );
                    }
                  : () => {
                      /** Noop. */
                    }
              }
            >
              <span className="btn-text">Transfer to agent</span>
            </button>
            <button
              className={
                'long-btn  ' +
                ' ' +
                (canTransferToQueueConversation === false ? 'disabled' : '')
              }
              onClick={
                canTransferToQueueConversation === true
                  ? () => {
                      this.props.transferToQueueModalOpen({
                        csid: this.props.metaConversation.conversation.id,
                      });
                    }
                  : () => {
                      /** Noop. */
                    }
              }
            >
              <span className="btn-text">Transfer to queue</span>
            </button>
            <button
              className={
                'long-btn last  ' +
                ' ' +
                (canTransferToNumberConversation === false ? 'disabled' : '')
              }
              onClick={
                canTransferToNumberConversation === true
                  ? () => {
                      this.props.transferToNumberModalOpen(
                        this.props.metaConversation,
                      );
                    }
                  : () => {
                      /** Noop. */
                    }
              }
            >
              <span className="btn-text">Transfer to number</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
}
